"use strict";

// Main navigation hover intent
//
// The main menu wraps onto several rows while the mega menu panels open below
// the whole header. Moving the pointer from an item on the first row down to
// its panel crosses the items of the rows underneath, and their plain CSS
// :hover used to swap the open panel straight away. An item on a lower row now
// only takes over once the pointer settles on it, and the panel that is already
// open is held visible while the pointer travels towards it.
(() => {
  // How long the pointer has to settle on a lower row item before its menu opens.
  const SWITCH_DELAY = 220;
  // Upper bound for the hold, so a menu always opens even if the pointer keeps moving.
  const MAX_HOLD = 1200;

  const canHover = () => {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  };

  const isOnLowerRow = (item, reference) => {
    return item.getBoundingClientRect().top >= reference.getBoundingClientRect().bottom - 1;
  };

  const hasSubMenu = (item) => {
    return item.classList.contains("has-sub-menu");
  };

  const setupMenu = (menu) => {
    if (menu.dataset.hoverIntent === "true") {
      return;
    }

    // Items without a dropdown take part too: they can sit on the row that the
    // pointer crosses on its way to an open panel.
    const items = menu.querySelectorAll(":scope > .navigation-item");

    if (!menu.querySelector(":scope > .has-sub-menu")) {
      return;
    }

    menu.dataset.hoverIntent = "true";

    let openItem = null;
    let pendingItem = null;
    let switchTimer = null;
    let pendingStart = 0;
    let lastY = null;

    const clearSwitchTimer = () => {
      clearTimeout(switchTimer);
      switchTimer = null;
    };

    const cancelPending = () => {
      clearSwitchTimer();

      if (pendingItem) {
        pendingItem.classList.remove("nav-hover-pending");
        pendingItem = null;
      }

      if (openItem) {
        openItem.classList.remove("nav-hover-hold");
      }
    };

    const activate = (item) => {
      cancelPending();
      openItem = item;
    };

    const startSwitchTimer = () => {
      clearSwitchTimer();
      switchTimer = setTimeout(() => {
        const item = pendingItem;

        if (!item) {
          return;
        }

        item.classList.remove("nav-hover-pending");
        pendingItem = null;
        activate(hasSubMenu(item) ? item : null);
      }, SWITCH_DELAY);
    };

    items.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        if (!canHover()) {
          return;
        }

        // The pointer came back to the open menu, either on its link or inside its panel.
        if (item === openItem) {
          cancelPending();
          return;
        }

        // Nothing open yet, or the item sits on the same row as the open one, so
        // it cannot be on the way to the open panel: switch straight away.
        if (!openItem || !isOnLowerRow(item, openItem)) {
          activate(hasSubMenu(item) ? item : null);
          return;
        }

        cancelPending();
        pendingItem = item;
        pendingStart = Date.now();
        lastY = null;
        item.classList.add("nav-hover-pending");
        openItem.classList.add("nav-hover-hold");
        startSwitchTimer();
      });

      item.addEventListener("mousemove", (event) => {
        if (item !== pendingItem || Date.now() - pendingStart > MAX_HOLD) {
          return;
        }

        // Still travelling across the item, keep waiting for the pointer to settle.
        if (lastY !== null && Math.abs(event.clientY - lastY) >= 1) {
          startSwitchTimer();
        }

        lastY = event.clientY;
      });

      item.addEventListener("mouseleave", () => {
        if (item === pendingItem) {
          cancelPending();
        }
      });
    });

    menu.addEventListener("mouseleave", () => {
      cancelPending();
      openItem = null;
    });
  };

  const setup = () => {
    document.querySelectorAll(".navigation-main").forEach(setupMenu);
  };

  setup();
  document.addEventListener("shopify:section:load", setup);
})();
